/**
 * Sending a file from the browser straight to storage (rev6 Part 3, D22): Azure Put Block pieces
 * to a link our API made. No axios here, so it can be tested on its own.
 */

/** Links are full Azure URLs, or "/api/..." on our backend when it stores files on a laptop. */
export const absoluteUrl = (url) => (url && url.startsWith('/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url);

/** Piece n's id: six digits, base64, so every id has the same length (Azure requires it). */
export const pieceId = (n) => btoa(String(n).padStart(6, '0'));

export const pieceCount = (size, pieceBytes) => Math.ceil(size / pieceBytes);

/** Bytes already sent, from the piece ids storage has. */
export const receivedBytes = (received, size, pieceBytes) => {
    const count = pieceCount(size, pieceBytes);
    const have = new Set(received);
    let total = 0;
    for (let i = 0; i < count; i++) {
        if (have.has(pieceId(i))) total += i < count - 1 ? pieceBytes : size - (count - 1) * pieceBytes;
    }
    return total;
};

const withQuery = (url, params) => `${url}${url.includes('?') ? '&' : '?'}${new URLSearchParams(params)}`;

/**
 * One Azure Put Block. XMLHttpRequest, not fetch, because only it reports upload progress.
 * Rejects with { status } for a refusal, { network: true } for a dropped connection, or
 * { aborted: true } when signal aborts.
 */
export const putPiece = (url, blob, onProgress, signal) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.upload.onprogress = (e) => onProgress && onProgress(e.loaded);
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject({ status: xhr.status }));
        xhr.onerror = () => reject({ network: true });
        xhr.onabort = () => reject({ aborted: true });
        if (signal) {
            if (signal.aborted) return reject({ aborted: true });
            signal.addEventListener('abort', () => xhr.abort(), { once: true });
        }
        xhr.send(blob);
    });

/**
 * Sends a file's pieces one after another, skipping those storage already has (Retry). When
 * storage refuses a piece because the link ran out, it asks our API for a fresh link once and
 * carries on. onProgress(bytesSent) covers the whole file.
 */
export const sendPieces = async ({ file, pieceBytes, url, received = [], onProgress, freshLink, signal, put = putPiece }) => {
    const count = pieceCount(file.size, pieceBytes);
    const have = new Set(received);
    let link = url;
    let done = receivedBytes(received, file.size, pieceBytes);
    onProgress && onProgress(done);
    const send = (to, id, piece, before) =>
        put(withQuery(absoluteUrl(to), { comp: 'block', blockid: id }), piece, (loaded) => onProgress && onProgress(before + loaded), signal);
    for (let i = 0; i < count; i++) {
        const id = pieceId(i);
        if (have.has(id)) continue;
        const piece = file.slice(i * pieceBytes, Math.min(file.size, (i + 1) * pieceBytes));
        try {
            await send(link, id, piece, done);
        } catch (e) {
            if (e?.status !== 403 || !freshLink) throw e;
            link = await freshLink();
            await send(link, id, piece, done);
        }
        done += piece.size;
        onProgress && onProgress(done);
    }
};
